import { useNavigate } from "react-router-dom";
import omit from "lodash-es/omit";
import {
    Grid,
    GridProps,
    InternalStandardProps,
    Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { Category, Content } from "JS/Models";
import { videoCategoryTitleToString } from "JS/Helpers";
import { useRouting } from "JS/React/Hooks/Routes";
import { useThemeColors } from "JS/React/Hooks/ThemeColors";
import { VideoCategoryTitle } from "..";
import { RenderIf } from "JS/React/Components/Wrapper/RenderIf";

type PaperProps = {
    bgColor: string[];
};

const Item = styled(Paper)<PaperProps>(({ theme, bgColor }) => ({
    ...(bgColor && {
        background: `linear-gradient(180deg, ${bgColor.join(",")})`,
    }),
    ...theme.typography.body1,
    padding: theme.spacing(1),
    textAlign: "center",
    minHeight: "10rem",
    color: theme.palette.grey[200],
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "end",
    borderRadius: "0",
    boxShadow: "none",
    cursor: "pointer",
}));

const CategoryTextWrapper = styled("div")({
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "90%",
});

export interface VideoCategoryListProps
    extends InternalStandardProps<GridProps> {
    videos: Content[];
    categorizedVideos: Category[];
}

export const VideoCategoryList = (props: VideoCategoryListProps) => {
    const { className, videos, categorizedVideos, ...rest } = props;

    const navigate = useNavigate();
    const { linkProvider } = useRouting();
    const { darkToneColors, lightToneColors, mediumToneColors } =
        useThemeColors();

    const updatedVideoCategoryTitle = omit(VideoCategoryTitle, "INDEX");

    const getBgColor = (idx: number, isCategorized: boolean): string[] => {
        if (isCategorized) {
            const populatedItems = Object.keys(
                updatedVideoCategoryTitle,
            ).length;
            return (populatedItems + idx) % 2 === 0
                ? lightToneColors
                : mediumToneColors;
        } else {
            return idx === 0
                ? darkToneColors
                : idx % 2 === 0
                ? lightToneColors
                : mediumToneColors;
        }
    };

    return (
        <Grid {...rest} container spacing={0.2}>
            {Object.values(updatedVideoCategoryTitle).map(
                (item, idx) =>
                    (item !== updatedVideoCategoryTitle.MY_VIDEOS ||
                        videos?.filter(
                            (video) =>
                                video.publish_free === "1" ||
                                video.is_purchased,
                        )?.length > 0) && (
                        <Grid
                            item={true}
                            xs={idx === 0 ? 12 : 6}
                            md={3}
                            key={item}
                            onClick={() =>
                                navigate(
                                    linkProvider.react.videos().listing(item),
                                )
                            }
                        >
                            <Item bgColor={getBgColor(idx, false)}>
                                {videoCategoryTitleToString(item)}
                            </Item>
                        </Grid>
                    ),
            )}

            <RenderIf isTrue={!!categorizedVideos?.length}>
                {categorizedVideos
                    .filter((d) => d.category_title !== "All Videos")
                    .map((item, idx) => (
                        <Grid
                            item={true}
                            xs={6}
                            md={3}
                            key={item.category_id}
                            onClick={() =>
                                navigate(
                                    linkProvider.react
                                        .videos()
                                        .listing(`${item.category_id}`),
                                )
                            }
                        >
                            <Item bgColor={getBgColor(idx, true)}>
                                <CategoryTextWrapper>
                                    {item.category_title}
                                </CategoryTextWrapper>
                            </Item>
                        </Grid>
                    ))}
            </RenderIf>
        </Grid>
    );
};
